import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  subtitle?: string;
  type: ToastType;
  action?: ToastAction;
  createdAt: number;
}

interface ShowToastOptions {
  subtitle?: string;
  action?: ToastAction;
  durationMs?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: ShowToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const DEFAULT_DURATION_MS: Record<ToastType, number> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: 10000,
};

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-success text-success-foreground',
  error: 'bg-destructive text-destructive-foreground',
  warning: 'bg-warning text-warning-foreground',
  info: 'bg-info text-info-foreground',
};

const DUPLICATE_WINDOW_MS = 10000;
const MAX_QUEUE_AGE_MS = 30000;

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<Toast | null>(null);
  const recentRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<Toast[]>([]);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNext = useCallback(() => {
    const now = Date.now();
    // Remove expired items from queue
    queueRef.current = queueRef.current.filter((t) => now - t.createdAt < MAX_QUEUE_AGE_MS);

    if (queueRef.current.length === 0) {
      setToast(null);
      return;
    }

    const next = queueRef.current.shift()!;
    setToast(next);

    const duration = DEFAULT_DURATION_MS[next.type];
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => {
      setToast(null);
      setTimeout(showNext, 100);
    }, duration);
  }, []);

  const dismiss = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setToast(null);
    setTimeout(showNext, 100);
  }, [showNext]);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', options?: ShowToastOptions) => {
      const dedupeKey = `${type}:${message}`;
      if (recentRef.current.has(dedupeKey)) return;
      recentRef.current.add(dedupeKey);
      setTimeout(() => recentRef.current.delete(dedupeKey), DUPLICATE_WINDOW_MS);

      const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const duration = options?.durationMs ?? (options?.action ? 8000 : DEFAULT_DURATION_MS[type]);

      const newToast: Toast = {
        id,
        message,
        subtitle: options?.subtitle,
        type,
        action: options?.action,
        createdAt: Date.now(),
      };

      // If nothing showing, display immediately
      setToast((current) => {
        if (!current) {
          if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = setTimeout(() => {
            setToast(null);
            setTimeout(showNext, 100);
          }, duration);
          return newToast;
        }
        // Something already showing — queue it
        queueRef.current.push(newToast);
        return current;
      });
    },
    [showNext]
  );

  // Memoise on the stable showToast reference so internal toast state
  // changes (which re-render the provider to draw the toast UI below)
  // don't invalidate every consumer of useToast across the app.
  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[100] flex justify-center px-4"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div
            className={`${TYPE_STYLES[toast.type]} w-full max-w-prose px-4 py-2.5 rounded-lg shadow-lg animate-[slideUp_0.2s_ease-out]`}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{toast.message}</span>
                {toast.subtitle && (
                  <p className="text-xs mt-0.5 opacity-90">{toast.subtitle}</p>
                )}
              </div>
              <button
                onClick={dismiss}
                className="opacity-70 hover:opacity-100 text-sm leading-none shrink-0 px-1"
              >
                &times;
              </button>
            </div>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action!.onClick();
                  dismiss();
                }}
                className="mt-1.5 text-xs font-semibold underline underline-offset-2 opacity-90 hover:opacity-100"
              >
                {toast.action.label}
              </button>
            )}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};
